import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Radio,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Link as MuiLink } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { COLORS } from "colors/color";
import TextDivider from "components/Deviders/textDevider";
import { GoogleLogin } from "react-google-login";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material";
import linkedinLogo from "../../assets/img/linkedin.png";
import googleLogo from "../../assets/img/google.png";
import colors from "colors/color";

import opened_eye_icon from "../../assets/img/open_eye_icon.svg";
import closed_eye_icon from "../../assets/img/closed_eye_icon.svg";
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    marginTop: 2,
    gap: 10,
    borderRadius: "15px",
    border: "1px solid #D9D9D9",
    background: "#FFFFFF",
    transition: "border-color 0.4s ease, box-shadow 0.4s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      transition: "0.3s ease",
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0017E7",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "2px",
      boxShadow: "0px 0px 5px 0.4px #0017E7",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "8px 14px",
    },
  },
}));

const REQUEST_TIMEOUT = 30000;

const SignIn = ({
  browserFingerprint,
  captchaRequired,
  recaptchaRef,
  toggleAuthMode,
  onSignInSuccess,
  onGoogleSuccess,
  onLinkedInAuth,
  setSnackbar,
  setCaptchaRequired,
  clientId,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [checked, setChecked] = useState(false);
  const signInButtonRef = useRef(null);

  // Function to handle key down events on input fields
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (signInButtonRef.current) {
        signInButtonRef.current.click();
      }
    }
  };

  // Expose the handleConnexion function globally so it can be called externally
  React.useEffect(() => {
    window.handleSignInClick = handleConnexion;
    
    // Cleanup function to remove the global function when component unmounts
    return () => {
      delete window.handleSignInClick;
    };
  }, [login, password, captchaRequired, recaptchaToken]);

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };
  const handleCaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleGoogleSuccess = (res) => {
    console.log("Login Success: currentUser:", res.profileObj);
    onGoogleSuccess(res);
  };

  const handleGoogleFailure = (res) => {
    console.error("Login failed: res:", res);
  };

  const handleConnexion = async () => {
    try {
      if (!login || !password) {
        setSnackbar({
          open: true,
          message: t("login.errors.email_password_required"),
          severity: "error",
        });
        return;
      }

      if (captchaRequired && !recaptchaToken) {
        setSnackbar({
          open: true,
          message: t("login.errors.captcha_required"),
          severity: "warning",
        });
        return;
      }

      const requestData = {
        email: login,
        password: password,
        browserFingerprint: browserFingerprint,
      };

      if (captchaRequired) {
        requestData.recaptcha_token = recaptchaToken;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}authentication/login/`,
        requestData,
        {
          signal: controller.signal,
          timeout: REQUEST_TIMEOUT,
        }
      );

      clearTimeout(timeoutId);

      if (response.data.status === "success") {
        setLogin("");
        setPassword("");
        setCaptchaRequired(false);
        setRecaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        onSignInSuccess(response);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ECONNABORTED") {
        console.log("Request timed out, but error suppressed.");
        return;
      }

      if (err.response?.status === 429 && err.response?.data?.time_left) {
        const timeLeft = err.response.data.time_left;
        setSnackbar({
          open: true,
          message: t("login.errors.too_many_attempts", { timeLeft }),
          severity: "warning",
        });
        return;
      }

      if (err.response?.data?.captcha_required) {
        setCaptchaRequired(true);
      } else {
        setCaptchaRequired(false);
      }

      const errorMessage =
        err.response?.data?.detail || t("login.errors.unexpected_error");
      const errorCode = err.response?.data?.code || "UNKNOWN_ERROR";

      let snackbarMessage = "";
      let severity = "error";

      switch (errorCode) {
        case "CAPTCHA_REQUIRED":
          snackbarMessage = t("login.errors.captcha_required");
          severity = "warning";
          break;
        case "INVALID_CAPTCHA":
          snackbarMessage = t("login.errors.invalid_captcha");
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          break;
        case "MISSING_FIELDS":
          snackbarMessage = t("login.errors.missing_fields");
          break;
        case "INVALID_EMAIL_FORMAT":
          snackbarMessage = t("login.errors.invalid_email_format");
          break;
        case "INVALID_CREDENTIALS":
          snackbarMessage = t("login.errors.invalid_credentials");
          break;
        case "ACCOUNT_INACTIVE":
          snackbarMessage = t("login.errors.account_inactive");
          severity = "warning";
          break;
        case "ACCOUNT_LOCKED":
          snackbarMessage = t("login.errors.account_locked");
          severity = "warning";
          break;
        case "TOKEN_GENERATION_FAILED":
          snackbarMessage = t("login.errors.token_generation_failed");
          break;
        case "NETWORK_ERROR":
          snackbarMessage = t("login.errors.network_error");
          break;
        default:
          snackbarMessage = errorMessage;
      }

      setSnackbar({
        open: true,
        message: snackbarMessage,
        severity: severity,
      });
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          color: "#00000066",
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: 12,
        }}
      >
        {t("login.enter_email")}
      </Typography>
      <StyledTextField
        required
        fullWidth
        value={login}
        onChange={(e) => setLogin(e.target.value)}
        variant="outlined"
        sx={{ mb: 2 }}
        onKeyDown={handleKeyDown}
      />
      <Typography
        sx={{
          color: "#00000066",
          fontFamily: "Poppins",
          fontWeight: 500,
          fontSize: 12,
        }}
      >
        {t("login.enter_password")}
      </Typography>
      <StyledTextField
        required
        fullWidth
        type={showPassword ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        variant="outlined"
        sx={{ width: "100%", fontFamily: "Poppins" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                sx={{ marginRight: 1 }}
              >
                <img
                  src={showPassword ? opened_eye_icon : closed_eye_icon}
                  alt={showPassword ? "Hide password" : "Show password"}
                  style={{
                    width: "15px",
                    height: "15px",
                    filter: "none",
                    opacity: 1,
                  }}
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
        onKeyDown={handleKeyDown}
      />

      {captchaRequired && (
        <div className="captcha-container mt-4">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />
        </div>
      )}
      <Grid container>
        <Grid item xs={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={handleChange}
                sx={{
                  color: colors.primary,
                  "&.Mui-checked": {
                    color: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: colors.primary,
                }}
              >
                Remember me
              </Typography>
            }
          />
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <MuiLink
            component={RouterLink}
            to="/reset-password"
            variant="body2"
            sx={{
              display: "block",
              mt: 1,
              mb: 2,
              fontFamily: "Poppins",
              fontWeight: 400,
              fontSize: "14px",
              color: colors.primary,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "none"
              }
            }}
          >
            {t("login.forgot_password")}
          </MuiLink>
        </Grid>
      </Grid>

      <Grid item container>
        <Grid item xs={12} md={12}>
          <Button
            ref={signInButtonRef}
            fullWidth
            variant="contained"
            onClick={handleConnexion}
            sx={{
              mb: 1,
              p: { xs: 1, md: 1.3 },
              color: "white",
              borderRadius: 20,
              fontFamily: "Poppins",
              textTransform: "none",
              fontSize: { xs: "14px", md: "16px" },
              borderRadius: "15px",
              gap: 10,
              background: "#0017E7",
              fontWeight: 500,
            }}
          >
            {t("login.sign_in")}
          </Button>
        </Grid>
        <Grid item xs={12} md={12}>
          <Button
            fullWidth
            variant="outlined"
            onClick={toggleAuthMode}
            sx={{
              mb: 1,
              p: { xs: 1, md: 1.3 },
              color: colors.primary,
              border: "1px solid #0017E7",
              borderRadius: 20,
              fontFamily: "Poppins",
              textTransform: "none",
              fontSize: { xs: "14px", md: "14px" },
              borderRadius: "15px",
              gap: 10,
              background: "#fff",
              fontWeight: 500,
            }}
          >
            {t("login.not_registered")}
          </Button>
        </Grid>
      </Grid>

      <Typography
        variant="body2"
        align="center"
        sx={{ my: 2, color: "rgba(0,0,0,0.3)" }}
      >
        <TextDivider>{t("login.or_continue_with")}</TextDivider>
      </Typography>

      <Grid item container spacing={0}>
        <Grid item xs={12} md={12}>
          <GoogleLogin
            clientId={clientId}
            onSuccess={handleGoogleSuccess}
            onFailure={handleGoogleFailure}
            cookiePolicy={"single_host_origin"}
            isSignedIn={false}
            render={({ onClick }) => (
              <Button
                fullWidth
                variant="outlined"
                onClick={onClick}
                sx={{
                  mb: 1,
                  minWidth: "307px",
                  p: { xs: 1, md: 1.3 },
                  color: colors.primary,
                  border: "1px solid #0017E7",
                  borderRadius: 20,
                  fontFamily: "Poppins",
                  textTransform: "none",
                  fontSize: { xs: "14px", md: "14px" },
                  borderRadius: "15px",
                  gap: 2,
                  background: "#fff",
                  fontWeight: 500,
                  height: 50,
                }}
              >
                <img
                  src={googleLogo}
                  alt="Google Logo"
                  style={{
                    width: "20px",
                    height: "20px",
                    //   marginRight: "8px",
                  }}
                />
                <Typography>{t("login.sign_in_with_google")}</Typography>
              </Button>
            )}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onLinkedInAuth}
            sx={{
              mb: 1,
              // minWidth:'307px',
              p: { xs: 1, md: 1.3 },
              color: colors.primary,
              border: "1px solid #0017E7",
              borderRadius: 20,
              fontFamily: "Poppins",
              textTransform: "none",
              fontSize: "14px",
              borderRadius: "15px",
              gap: 2,
              background: "#fff",
              fontWeight: 500,
              height: 50,
            }}
          >
            <img
              src={linkedinLogo}
              alt="LinkedIn Logo"
              style={{
                width: "20px",
                height: "20px",
              }}
            />
            <Typography>{t("login.sign_in_with_linkedin")}</Typography>
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SignIn;
